import React, {
  isValidElement,
  useContext,
  useRef,
  type AllHTMLAttributes,
} from 'react';
import cx from 'classnames';
import useUpdateEffect from '@mc/hooks/useUpdateEffect';
import TabContext, { type TabContextType } from './TabContext';
import getTabId from './getTabId';
import stylesheet from './Tab.css';

export type TabProps = {
  index?: number;
  onClick?: () => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  disabled?: boolean;
} & AllHTMLAttributes<HTMLSpanElement>;

function Tab({
  index,
  onClick,
  children,
  prefix,
  suffix,
  disabled,
  ...props
}: TabProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const {
    id,
    currentIndex,
    setCurrentIndex,
    size,
    focusIndex,
    setFocusIndex,
    isManual,
    orientation,
    showOutline,
    setShowOutline,
    variant,
  } = useContext(TabContext) as TabContextType;
  const tabId = getTabId(id, index);
  const isCurrentTab = currentIndex === index;
  const isFocused = focusIndex === index;

  const newSize = size === 'large' ? 'large' : 'medium';

  const shouldTabShowOutline = () => {
    return showOutline && isFocused;
  };

  const classList = cx(
    stylesheet.root,
    stylesheet[newSize],
    shouldTabShowOutline() && stylesheet['focus-outline'],
    variant === 'chips' && stylesheet.chip,
    orientation === 'vertical' && stylesheet.vertical,
  );

  useUpdateEffect(() => {
    if (isFocused && ref.current) {
      ref.current.focus({ preventScroll: true });
    }
  }, [index, isCurrentTab, isFocused, isManual]);

  const handleClick = () => {
    if (!disabled && setCurrentIndex && setShowOutline && setFocusIndex) {
      setCurrentIndex(index ?? 0);
      setFocusIndex(index ?? 0);
      setShowOutline(false);
      if (onClick) {
        onClick();
      }
    }
  };

  const getTabIndex = () => {
    const trackedIndex = isManual ? focusIndex : currentIndex;

    return trackedIndex === index ? 0 : -1;
  };

  return (
    <span
      className={classList}
      ref={ref}
      role="tab"
      id={tabId}
      tabIndex={getTabIndex()}
      aria-selected={isCurrentTab}
      aria-controls={tabId && `${tabId}:panel`}
      onClick={() => handleClick()}
      {...(disabled && { 'aria-disabled': true })}
      {...props}
    >
      {prefix}
      <span>{children}</span>
      {isValidElement(suffix)
        ? suffix
        : suffix && <span className={stylesheet.suffixBadge}>{suffix}</span>}
    </span>
  );
}

export default Tab;
